import * as needleCloud from "needle-cloud";
import { BUILD_PLATFORM } from "../../../base/common/$__metadata.js";
import { Emitter, Event } from "../../../base/common/events.js";
import { Disposable } from "../../../base/common/lifecycle.js";
import { createDecorator } from "../../instantiation/common/registry.js";
import { ILogService } from "../../log/common/log.js";
import { IProductService } from "../../product/common/productService.js";
import { inspectorAccessReason } from "./inspectorAccess.js";
import { IIdentitiyService } from "./auth.js";

/**
 * Product-name payment calls landed in needle-cloud after some published builds,
 * so they are feature-detected the same way chatAI does for `aiComplete` — an
 * older SDK simply keeps the hand-rolled fetch below instead of crashing.
 *
 * Both bake the same api endpoint the editor resolves itself (dev
 * localhost:8081, prod <cloud>/api), so behaviour is unchanged; the SDK owns the
 * url building, auth header and JSON-mode handshake from here on.
 */
type PaymentSdk = {
    getProductInformation?(product: string, options?: { price?: string; baseUrl?: string }): Promise<PurchaseInfo | null>;
    checkoutProduct?(product: string, options?: { price?: string; promoCode?: string; app?: string; mode?: number; baseUrl?: string }): Promise<{ url: string } | null>;
};
const paymentSdk = needleCloud as PaymentSdk;

/**
 * Product identifiers as stored in the stripe product `metadata.type` field.
 * These strings are the canonical product ids across stripe, the needle-cloud
 * backend (`License.type`) and the `x-needle-product` query header.
 */
export const PRODUCT_NEEDLE_ENGINE = "Needle Engine";
export const PRODUCT_NEEDLE_INSPECTOR = "Needle Inspector";

/**
 * Stripe Price `lookup_key` of the Inspector license — sent as `?price=` on
 * BOTH payment endpoints so the price we DISPLAY is provably the price checkout
 * CHARGES.
 *
 * Without it the backend resolves a price by product search +
 * `prices.list({ limit: 1 })`, which returns newest-created first: adding any
 * second price to the Inspector product in Stripe silently changes what we
 * advertise, with no deploy and no signal. (That is not hypothetical — adding
 * one took the price display down entirely on 2026-08-01.)
 *
 * The key names a ROLE, not a version: to raise the price, create the new Price
 * in Stripe with `transfer_lookup_key: true` and both display and checkout
 * follow with no release here.
 *
 * MUST match the Stripe Price's lookup_key and needle-cloud's price-key
 * allowlist exactly — a key the server does not allowlist is rejected with 400
 * before any fallback runs, which would cost us the price AND the buy button.
 * The extension's service worker sends the same key (it builds its own URLs).
 */
export const PRICE_NEEDLE_INSPECTOR_LICENSE = "needle_inspector_license";

/**
 * A license as returned by the needle-cloud licenses API
 * (`/v1/account/get/licenses`). Minimal mirror of the backend `License` union —
 * seat-based subscriptions (Needle Engine tiers) and one-time purchases
 * (e.g. Needle Inspector) share this shape.
 */
export interface ActiveLicense {
    /** Product type from the stripe product metadata, e.g. "Needle Engine" or "Needle Inspector". */
    readonly type: string;
    /** Tier for seat-based licenses: "basic" | "indie" | "edu" | "pro" | "enterprise". */
    readonly license?: string;
    /** "seat-based" | "usage-based" | "one-time-purchase" */
    readonly mode?: string;
    /** Subscription status, e.g. "active" | "trialing". One-time purchases have none. */
    readonly status?: string;
    /** Whether the license is currently usable. */
    readonly valid?: boolean;
}

/**
 * Price of a product as returned by the public payment info endpoint
 * (`/v1/payment/info`). Display-only — checkout always shows the
 * authoritative price.
 */
export interface PurchaseInfo {
    /** Amount in major currency units (e.g. 49 for €49). */
    readonly amount: number;
    /** ISO 4217 currency code as reported by stripe, lowercase (e.g. "eur"). */
    readonly currency: string;
    /**
     * Pre-sale amount when a discount is currently active (the dashboard-managed
     * `needle-inspector-discount` stripe coupon) — `amount` is then the reduced
     * price. Absent while no sale is running.
     */
    readonly original?: number;
    /** Sale display name (the stripe coupon's `name`), e.g. "Launch sale". */
    readonly label?: string;
    /**
     * Display tag for the BASE price, e.g. "Launch price" — dashboard-managed
     * via the Stripe product's `price_label` metadata. Independent of sales
     * (`label`/`original`); UIs should hide it while a sale runs.
     */
    readonly price_label?: string;
    /**
     * AI chat-token credits bundled with the purchase (dashboard-managed
     * `bonus_ai_tokens` metadata on the Stripe price/product; granted into
     * the buyer's credit ledger, valid 12 months). Absent when none.
     */
    readonly included_ai_tokens?: number;
    /**
     * When the sale ends (ms epoch, from the coupon's stripe-enforced
     * `redeem_by`). Absent for open-ended sales — show no deadline then.
     */
    readonly expires_at?: number;
}

export const ILicenseService = createDecorator<ILicenseService>("licenseService");

export type InspectorAccessReason = "license" | "free-weekend" | "free-site" | null;

export interface InspectorTimeResponse {
    readonly utc: string;
    readonly unixMilliseconds: number;
    readonly date: string;
    readonly day: string;
    readonly weekday: number;
    readonly hour: number;
    readonly timezone: string;
    readonly source: "server";
}

function isInspectorTimeResponse(value: unknown): value is InspectorTimeResponse {
    if (!value || typeof value !== "object") return false;
    const result = value as Partial<InspectorTimeResponse>;
    return typeof result.utc === "string"
        && typeof result.unixMilliseconds === "number"
        && Number.isFinite(result.unixMilliseconds)
        && typeof result.date === "string"
        && typeof result.day === "string"
        && Number.isInteger(result.weekday) && result.weekday >= 0 && result.weekday <= 6
        && typeof result.hour === "number"
        && Number.isInteger(result.hour)
        && typeof result.timezone === "string"
        && result.source === "server";
}

export interface ILicenseService {
    readonly _serviceBrand: undefined;

    /** Fires when the cached licenses change (login/logout, refresh). */
    readonly onDidChange: Event<ILicenseService>;

    /** Last fetched (valid) licenses — null while logged out or not yet loaded. */
    readonly licenses: readonly ActiveLicense[] | null;

    /** Active licenses for the current user. Cached; re-fetched when the login state changes. */
    getLicenses(): Promise<readonly ActiveLicense[]>;

    /**
     * True when the user holds a paid inspector entitlement: a Needle Engine
     * PRO (or enterprise) license, OR a Needle Inspector one-time purchase.
     */
    hasInspectorLicense(): Promise<boolean>;

    /**
     * Synchronous variant of {@link hasInspectorLicense} against the cached
     * licenses — for sync callsites like `IProperty.applyGuard`. Null while the
     * licenses have not been fetched yet (logged out or still loading).
     */
    get isInspectorLicensed(): boolean | null;

    /** Effective feature access. Paid ownership remains available separately above. */
    readonly isInspectorAccessAllowed: boolean;
    readonly inspectorAccessReason: InspectorAccessReason;
    /** Refresh temporary access using trusted server time. */
    refreshInspectorAccess(): Promise<void>;

    /** Force a re-fetch from the server, e.g. after returning from a checkout. */
    refresh(): Promise<readonly ActiveLicense[]>;

    /**
     * Checkout URL for buying the Needle Inspector license (the external
     * payment flow — opens a stripe checkout). Null when not logged in.
     * An optional promotion code is applied server-side — required while a
     * sale coupon auto-applies (stripe then hides checkout's own code field).
     */
    getPurchaseUrl(promoCode?: string): string | null;

    /**
     * Checkout URL for the one-time Inspector purchase. Prefers an
     * AUTHENTICATED server-created session (the checkout email is then
     * prefilled + locked to the account email); falls back to the plain
     * tab-navigation URL of {@link getPurchaseUrl} (editable email field).
     */
    getCheckoutUrl(promoCode?: string): Promise<string | null>;

    /**
     * Price of the Needle Inspector license for display in upsell UI.
     * Best-effort: resolves to null when the API is unavailable — callers
     * simply don't show a price then. Cached after the first successful fetch.
     */
    getPurchaseInfo(): Promise<PurchaseInfo | null>;
}

/** Tiers of the Needle Engine license that count as an inspector entitlement. */
const ENGINE_TIERS_WITH_INSPECTOR = ["pro", "enterprise"];

/** The inspector entitlement: Needle Engine PRO/enterprise OR a Needle Inspector purchase. */
function licensesIncludeInspector(licenses: readonly ActiveLicense[]): boolean {
    return licenses.some(l =>
        (l.type === PRODUCT_NEEDLE_ENGINE && l.license != undefined && ENGINE_TIERS_WITH_INSPECTOR.includes(l.license))
        || l.type === PRODUCT_NEEDLE_INSPECTOR
    );
}

export abstract class AbstractLicenseService extends Disposable implements ILicenseService {
    readonly _serviceBrand: undefined;

    protected readonly _onDidChange = this._register(new Emitter<ILicenseService>());
    readonly onDidChange = this._onDidChange.event;

    private _licenses: readonly ActiveLicense[] | null = null;
    private _refreshPromise: Promise<readonly ActiveLicense[]> | null = null;
    private _inspectorWeekend: boolean | null = null;
    private _accessRefreshPromise: Promise<void> | null = null;

    constructor(
        @ILogService protected readonly logger: ILogService,
        @IIdentitiyService protected readonly identity: IIdentitiyService,
        @IProductService protected readonly productService: IProductService,
    ) {
        super();
        // Licenses follow the login state: fetch on login, clear on logout.
        this._register(this.identity.onDidChange(() => {
            if (this.identity.isLoggedIn) {
                this.refresh().catch(err => this.logger.warn("LicenseService: refresh after login change failed", err));
            }
            else if (this._licenses !== null) {
                this._licenses = null;
                this._onDidChange.fire(this);
            }
        }));
        if (this.identity.isLoggedIn) {
            this.refresh().catch(err => this.logger.warn("LicenseService: initial refresh failed", err));
        }
    }

    get licenses(): readonly ActiveLicense[] | null {
        return this._licenses;
    }

    getLicenses(): Promise<readonly ActiveLicense[]> {
        if (this._licenses) return Promise.resolve(this._licenses);
        return this.refresh();
    }

    refresh(): Promise<readonly ActiveLicense[]> {
        if (this._refreshPromise) return this._refreshPromise;
        if (!this.identity.isLoggedIn) {
            this._licenses = null;
            return Promise.resolve([]);
        }
        this._refreshPromise = this.fetchLicenses()
            .then(res => {
                // Keep only usable licenses — the backend marks e.g. past-due
                // subscriptions with valid: false.
                const valid = (res || []).filter(l => l && l.valid !== false);
                this._licenses = valid;
                this.logger.info(`LicenseService: fetched ${valid.length} active license(s)`, valid.map(l => l.type + (l.license ? `:${l.license}` : "")));
                this._onDidChange.fire(this);
                return this._licenses;
            })
            .catch(err => {
                this.logger.error("LicenseService: failed to fetch licenses", err);
                return this._licenses || [];
            })
            .finally(() => {
                this._refreshPromise = null;
            });
        return this._refreshPromise;
    }

    async hasInspectorLicense(): Promise<boolean> {
        const licenses = await this.getLicenses();
        return licensesIncludeInspector(licenses);
    }

    get isInspectorLicensed(): boolean | null {
        if (this._licenses === null) return null;
        return licensesIncludeInspector(this._licenses);
    }

    get inspectorAccessReason(): InspectorAccessReason {
        return inspectorAccessReason(this.isInspectorLicensed, this._inspectorWeekend, undefined, this.supportsInspectorWeekend);
    }

    protected get supportsInspectorWeekend(): boolean {
        return BUILD_PLATFORM === "chrome-extension";
    }

    get isInspectorAccessAllowed(): boolean {
        return this.inspectorAccessReason !== null;
    }

    refreshInspectorAccess(): Promise<void> {
        if (this._accessRefreshPromise) return this._accessRefreshPromise;
        this._accessRefreshPromise = this.fetchInspectorTime()
            .then(result => {
                if (!isInspectorTimeResponse(result)) throw new Error("Inspector time endpoint returned an invalid response");
                const weekend = result.weekday === 0 || result.weekday === 6;
                const changed = this._inspectorWeekend !== weekend;
                this._inspectorWeekend = weekend;
                if (changed) this._onDidChange.fire(this);
            })
            .catch(error => {
                const changed = this._inspectorWeekend !== null;
                this._inspectorWeekend = null;
                if (changed) this._onDidChange.fire(this);
                throw error;
            })
            .finally(() => { this._accessRefreshPromise = null; });
        return this._accessRefreshPromise;
    }

    protected async fetchInspectorTime(): Promise<InspectorTimeResponse> {
        const response = await fetch(this._apiBaseUrl() + "/time");
        if (!response.ok) throw new Error(`Inspector time request failed: ${response.status} ${response.statusText}`);
        return response.json() as Promise<InspectorTimeResponse>;
    }

    getPurchaseUrl(promoCode?: string): string | null {
        const userId = this.identity.me?.id;
        if (!userId) return null;
        const url = new URL(this._apiBaseUrl() + "/v1/payment/create");
        url.searchParams.set("app", "needle-inspector");
        url.searchParams.set("user", userId);
        url.searchParams.set("product", PRODUCT_NEEDLE_INSPECTOR);
        // name the exact price rather than letting the server pick the
        // newest-created one — must match what getPurchaseInfo displays
        url.searchParams.set("price", PRICE_NEEDLE_INSPECTOR_LICENSE);
        // mode is an index into the payment api's available_modes: 1 = one-time payment
        url.searchParams.set("mode", "1");
        if (promoCode?.trim()) url.searchParams.set("promo", promoCode.trim());
        return url.toString();
    }

    async getCheckoutUrl(promoCode?: string): Promise<string | null> {
        try {
            const url = await this.fetchCheckoutUrl(promoCode);
            if (url) return url;
        }
        catch (err) {
            this.logger.warn("LicenseService: authenticated checkout-session fetch failed — falling back to the plain checkout URL", err);
        }
        return this.getPurchaseUrl(promoCode);
    }

    /**
     * Platform hook: create the checkout session AUTHENTICATED via the JSON
     * mode of needle-cloud's /v1/payment/create (Accept: application/json +
     * bearer token → {url}). The server derives the user from the token, so
     * the checkout email is prefilled + locked. Default: not available —
     * platforms with a token path override this (the chrome extension routes
     * through its service worker, which holds the logto client).
     */
    protected async fetchCheckoutUrl(promoCode?: string): Promise<string | null> {
        if (!paymentSdk.checkoutProduct) return null;
        const session = await paymentSdk.checkoutProduct(PRODUCT_NEEDLE_INSPECTOR, {
            price: PRICE_NEEDLE_INSPECTOR_LICENSE,
            promoCode: promoCode?.trim() || undefined,
            app: "needle-inspector",
            mode: 1, // index into the payment api's available_modes: one-time payment
            baseUrl: this._apiBaseUrl(),
        });
        return session?.url ?? null;
    }

    /** Client-side price cache TTL. Short so a sale starting/ending (the
     * dashboard-managed discount coupon) reaches long-running editors — the
     * dialog refetches per open, this only dedupes rapid re-opens. */
    private static readonly PURCHASE_INFO_TTL = 1000 * 60 * 5;
    private _purchaseInfo: { info: PurchaseInfo, fetchedAt: number } | null = null;
    private _purchaseInfoPromise: Promise<PurchaseInfo | null> | null = null;

    getPurchaseInfo(): Promise<PurchaseInfo | null> {
        // Cache successes briefly; failures are not cached so a temporarily
        // unreachable API can recover.
        if (this._purchaseInfo && Date.now() - this._purchaseInfo.fetchedAt < AbstractLicenseService.PURCHASE_INFO_TTL) {
            return Promise.resolve(this._purchaseInfo.info);
        }
        this._purchaseInfoPromise ??= this.fetchPurchaseInfo()
            .then(info => {
                if (info && typeof info.amount === "number" && typeof info.currency === "string") {
                    this._purchaseInfo = { info, fetchedAt: Date.now() };
                    return info;
                }
                return null;
            })
            .catch(err => {
                // Best-effort by design: no price shown when the API is unavailable.
                this.logger.info("LicenseService: purchase info unavailable", err?.message ?? err);
                return null;
            })
            .finally(() => {
                this._purchaseInfoPromise = null;
            });
        return this._purchaseInfoPromise;
    }

    /**
     * Fetch the display price from the public payment info endpoint. No auth
     * needed. The chrome extension overrides this to go through the service
     * worker (page-context fetches obey the HOST page's CSP).
     */
    protected async fetchPurchaseInfo(): Promise<PurchaseInfo | null> {
        // same price the checkout URL names — displaying one price and charging
        // another is the failure this key exists to prevent
        if (paymentSdk.getProductInformation) {
            return await paymentSdk.getProductInformation(PRODUCT_NEEDLE_INSPECTOR, { price: PRICE_NEEDLE_INSPECTOR_LICENSE, baseUrl: this._apiBaseUrl() });
        }
        const url = new URL(this._apiBaseUrl() + "/v1/payment/info");
        url.searchParams.set("product", PRODUCT_NEEDLE_INSPECTOR);
        url.searchParams.set("price", PRICE_NEEDLE_INSPECTOR_LICENSE);
        const res = await fetch(url.toString());
        if (!res.ok) return null;
        return await res.json() as PurchaseInfo;
    }

    /**
     * Base URL of the needle-cloud API. In dev the API runs on its own port
     * (localhost:8081, per the needle-cloud SDK dev constants), separate from
     * the cloud frontend (localhost:5173) — so target it directly and avoid
     * depending on an /api proxy. In prod/staging the API is under /api.
     */
    protected _apiBaseUrl(): string {
        return this.productService.environment === "dev"
            ? "http://localhost:8081"
            : (this.productService.needleCloudUrl ?? "https://cloud.needle.tools") + "/api";
    }

    /** Fetch the raw licenses for the logged-in user from the backend. */
    protected abstract fetchLicenses(): Promise<readonly ActiveLicense[] | null>;
}
